import { useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Mail, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/data/catalog";

export const NewsletterBox = ({ source = "home", compact = false }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "it";
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t("newsletter.consentErr")); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/public/newsletter/subscribe`, { email, consent, language: lang, source, website });
      if (r.data.already) toast.info(t("newsletter.already"));
      else setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("newsletter.err"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`relative overflow-hidden rounded-[28px] bg-[#111] border border-[#D4AF37]/35 if-grain text-white ${compact ? "p-7 sm:p-9" : "p-8 sm:p-12 lg:p-14"}`} data-testid={`newsletter-box-${source}`}>
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.7), transparent 70%)" }} aria-hidden="true" />
      <div className={`relative grid gap-8 items-center ${compact ? "" : "lg:grid-cols-[1.1fr_1fr]"}`}>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {t("newsletter.eyebrow")}</p>
          <h2 className={`font-display font-bold tracking-tight mt-3 leading-tight ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl lg:text-4xl"}`}>{t("newsletter.title")}</h2>
          <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-lg">{t("newsletter.sub")}</p>
        </div>
        {done ? (
          <div className="bg-[#0b0b0b] border border-[#D4AF37]/40 rounded-2xl p-6 flex items-start gap-3" data-testid="newsletter-success">
            <CheckCircle2 className="w-6 h-6 text-[#D4AF37] shrink-0" />
            <div><p className="font-display font-bold">{t("newsletter.okTitle")}</p><p className="text-sm text-white/55 mt-1">{t("newsletter.okSub")}</p></div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form">
            <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} name="website" aria-hidden="true"
              className="absolute -left-[9999px] w-px h-px opacity-0" data-testid="newsletter-honeypot" />
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("newsletter.placeholder")} data-testid="newsletter-email"
                className="flex-1 bg-[#0b0b0b] border border-white/15 focus:border-[#D4AF37] rounded-full px-5 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none transition-colors" />
              <button type="submit" disabled={loading} data-testid="newsletter-submit"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C766] disabled:opacity-50 text-black font-bold px-6 py-3.5 rounded-full text-sm whitespace-nowrap transition-colors">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{t("newsletter.cta")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <label className="flex items-start gap-2.5 text-[11px] text-white/55 leading-relaxed cursor-pointer" data-testid="newsletter-consent-label">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="newsletter-consent" className="mt-0.5 accent-[#D4AF37] w-3.5 h-3.5 shrink-0" />
              <span><Trans i18nKey="newsletter.consent" components={{ 1: <Link to="/legale/privacy" className="text-[#D4AF37] underline" /> }} /></span>
            </label>
          </form>
        )}
      </div>
    </section>
  );
};
